import { useState, useCallback, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";

export const useSearch = (delay: number = 400, syncWithUrl: boolean = true) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlQuery = syncWithUrl ? searchParams.get("search") || "" : "";

  const [query, setQueryState] = useState(urlQuery);
  const [isDebouncing, setIsDebouncing] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Синхронизация, если URL меняется извне (только если syncWithUrl = true)
  useEffect(() => {
    if (syncWithUrl) {
      setQueryState(urlQuery);
    }
  }, [urlQuery, syncWithUrl]);

  const setQuery = useCallback(
    (value: string) => {
      setQueryState(value);
      setIsDebouncing(true);

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        if (syncWithUrl) {
          setSearchParams(
            (prev) => {
              const next = new URLSearchParams(prev);
              if (value) next.set("search", value);
              else next.delete("search");
              return next;
            },
            { replace: true },
          );
        }
        setIsDebouncing(false);
      }, delay);
    },
    [delay, syncWithUrl, setSearchParams],
  );

  const clearSearch = useCallback(() => {
    setQueryState("");
    setIsDebouncing(false);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    if (syncWithUrl) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete("search");
          return next;
        },
        { replace: true },
      );
    }
  }, [syncWithUrl, setSearchParams]);

  return { query, setQuery, clearSearch, isDebouncing };
};
